'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

// Add error handling to prevent React Error #130 when component fails to load
const AnimatedPlanetScene = dynamic(() => import('./AnimatedPlanetScene').catch(() => ({ default: () => null })), {
  ssr: false,
  loading: () => null,
});

export function HeroAnimation() {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let resizeQuery: MediaQueryList | null = null;
    let motionQuery: MediaQueryList | null = null;

    const canAnimate = () => {
      if (typeof window === 'undefined') return false;
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return !prefersReduced;
    };

    const handleChange = () => setShouldRender(canAnimate());

    const enableAnimation = () => {
      if (canAnimate()) {
        setShouldRender(true);
      }
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as typeof window & { requestIdleCallback?: any }).requestIdleCallback(
          enableAnimation,
          { timeout: 1200 }
        );
      } else {
        timeoutId = setTimeout(enableAnimation, 600);
      }

      resizeQuery = window.matchMedia('(max-width: 640px)');
      motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const canUseEventListener =
        typeof resizeQuery.addEventListener === 'function' &&
        typeof motionQuery.addEventListener === 'function';

      if (canUseEventListener) {
        resizeQuery.addEventListener('change', handleChange);
        motionQuery.addEventListener('change', handleChange);
      } else if (typeof (resizeQuery as MediaQueryList & { addListener?: (listener: () => void) => void }).addListener === 'function') {
        (resizeQuery as MediaQueryList & { addListener: (listener: () => void) => void }).addListener(handleChange);
        (motionQuery as MediaQueryList & { addListener: (listener: () => void) => void }).addListener(handleChange);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (resizeQuery && motionQuery) {
        const canUseEventListener =
          typeof resizeQuery.removeEventListener === 'function' &&
          typeof motionQuery.removeEventListener === 'function';

        if (canUseEventListener) {
          resizeQuery.removeEventListener('change', handleChange);
          motionQuery.removeEventListener('change', handleChange);
        } else if (typeof (resizeQuery as MediaQueryList & { removeListener?: (listener: () => void) => void }).removeListener === 'function') {
          (resizeQuery as MediaQueryList & { removeListener: (listener: () => void) => void }).removeListener(handleChange);
          (motionQuery as MediaQueryList & { removeListener: (listener: () => void) => void }).removeListener(handleChange);
        }
      }
    };
  }, []);

  if (!shouldRender) {
    return null;
  }

  return <AnimatedPlanetScene />;
}


